<template>
  <div class="device-table-container">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Organization Devices</h5>
      <div class="d-flex align-items-center gap-3">
        <a-select
          v-model:value="localFilter"
          style="width: 160px"
          @change="handleFilterChange"
        >
          <a-select-option value="all">All Devices</a-select-option>
          <a-select-option value="assigned">Assigned</a-select-option>
          <a-select-option value="unassigned">Unassigned</a-select-option>
          <a-select-option value="exempt">Exempt</a-select-option>
        </a-select>
        <a-input-search
          v-model:value="searchText"
          placeholder="Search devices..."
          style="width: 200px"
          allow-clear
        />
      </div>
    </div>

    <a-table
      :columns="columns"
      :data-source="filteredDevices"
      :loading="loading"
      :pagination="pagination"
      bordered
      :scroll="{ x: 900 }"
      row-key="id"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'display_name'">
          {{ record.display_name || record.device_name || '-' }}
        </template>
        <template v-if="column.key === 'license_status'">
          <a-tag :color="getStatusColor(record.license_status)">
            {{ getStatusLabel(record.license_status) }}
          </a-tag>
        </template>
        <template v-if="column.key === 'assigned_at'">
          {{ record.assigned_at ? formatDate(record.assigned_at) : '-' }}
        </template>
        <template v-if="column.key === 'actions'">
          <template v-if="!isExempt(record)">
            <a-button
              v-if="record.license_status === 'unassigned'"
              type="primary"
              size="small"
              :disabled="availableLicenses <= 0"
              @click="$emit('assign', record)"
            >
              Assign
            </a-button>
            <a-button
              v-else-if="record.license_status === 'assigned'"
              type="primary"
              danger
              size="small"
              @click="$emit('revoke', record)"
            >
              Revoke
            </a-button>
          </template>
          <a-tooltip v-else title="Exempt devices do not require licenses">
            <span class="text-muted">N/A</span>
          </a-tooltip>
        </template>
      </template>
    </a-table>
  </div>
</template>

<script>
export default {
  name: 'AdminDeviceTable',
  props: {
    devices: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    filter: {
      type: String,
      default: 'all',
    },
    availableLicenses: {
      type: Number,
      default: 0,
    },
  },
  emits: ['filter-change', 'assign', 'revoke'],
  data() {
    return {
      localFilter: this.filter,
      searchText: '',
      pagination: {
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ['10', '20', '50'],
        showTotal: (total) => `Total ${total} devices`,
      },
      columns: [
        {
          title: 'Display Name',
          dataIndex: 'display_name',
          key: 'display_name',
          width: '30%',
        },
        {
          title: 'Serial Number',
          dataIndex: 'serial_number',
          key: 'serial_number',
          width: '25%',
        },
        {
          title: 'Status',
          dataIndex: 'license_status',
          key: 'license_status',
          width: '12%',
          align: 'center',
        },
        {
          title: 'Assigned Date',
          dataIndex: 'assigned_at',
          key: 'assigned_at',
          width: '20%',
        },
        {
          title: 'Actions',
          key: 'actions',
          width: '13%',
          align: 'center',
        },
      ],
    };
  },
  computed: {
    filteredDevices() {
      if (!this.searchText) return this.devices;
      const search = this.searchText.toLowerCase();
      return this.devices.filter(
        (device) =>
          device.device_name?.toLowerCase().includes(search) ||
          device.display_name?.toLowerCase().includes(search) ||
          device.serial_number?.toLowerCase().includes(search)
      );
    },
  },
  watch: {
    filter(newVal) {
      this.localFilter = newVal;
    },
  },
  methods: {
    handleFilterChange(value) {
      this.$emit('filter-change', value);
    },
    isExempt(record) {
      return record.license_status === 'exempt' || record.is_license_exempt;
    },
    getStatusColor(status) {
      const colors = {
        assigned: 'green',
        unassigned: 'default',
        exempt: 'blue',
      };
      return colors[status] || 'default';
    },
    getStatusLabel(status) {
      const labels = {
        assigned: 'Assigned',
        unassigned: 'Unassigned',
        exempt: 'Exempt',
      };
      return labels[status] || status;
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
  },
};
</script>

<style scoped>
.device-table-container {
  background: #fff;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.gap-3 {
  gap: 12px;
}
</style>
